import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { deleteRecord } from "../../services/api.service";

/**
 * Shared delete flow for every master list.
 *
 * Usage:
 *   const del = useDeleteRecord({ modelName: "uom_masters", label: "UOM", onDeleted: refetch });
 *   ... generateColumns(..., { onDelete: del.requestDelete })
 *   <DeleteConfirmModal {...del.modalProps} />
 *
 * The dependency check lives on the server. When it refuses the delete we flip
 * the same popup into a "cannot delete" state showing the reason, so the user
 * never has to guess why nothing happened.
 */
const useDeleteRecord = ({ modelName, label = "record", onDeleted }) => {
  const [targetId, setTargetId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [dependencies, setDependencies] = useState([]);

  // Opens the confirmation popup for a row.
  const requestDelete = useCallback((id) => {
    setBlockedMessage("");
    setDependencies([]);
    setTargetId(id);
  }, []);

  const closeModal = useCallback(() => {
    setTargetId(null);
    setBlockedMessage("");
    setDependencies([]);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!targetId) return;

    try {
      setIsDeleting(true);

      const response = await deleteRecord({
        modelName,
        schema: localStorage.getItem("schema"),
        id: targetId,
      });

      if (response?.code === 1) {
        toast.success(response?.message || `${label} deleted successfully`);
        closeModal();
        if (onDeleted) onDeleted();
        return;
      }

      // Blocked by a dependency (or any other server side refusal) - keep the
      // popup open and show the reason instead of a toast that disappears.
      setBlockedMessage(
        response?.message || `This ${label} cannot be deleted.`
      );
      setDependencies(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong please try again!");
      closeModal();
    } finally {
      setIsDeleting(false);
    }
  }, [targetId, modelName, label, onDeleted, closeModal]);

  return {
    requestDelete,
    modalProps: {
      isOpen: Boolean(targetId),
      onClose: closeModal,
      onConfirm: confirmDelete,
      isDeleting,
      blockedMessage,
      dependencies,
      title: `Delete ${label}`,
      message: `Are you sure you want to delete this ${label}? This action cannot be undone.`,
    },
  };
};

export default useDeleteRecord;
